// The tag-filter + open-now control row (task-033, F-018).
//
// A combinable multi-select: each tag from the locator config is a toggle chip,
// plus an "Open now" toggle and a "Clear all" affordance. Toggling anything
// applies INSTANTLY — there is no Apply button — by calling back into the
// component, which re-runs the viewport query with the new tags[]/openNow. All
// state here is IN-MEMORY ONLY and rebuilt on every reconnect, like the rest of
// the component.
//
// HONEST COUNTS (F-018): each chip shows how many locations in the CURRENT
// viewport carry that tag, refreshed from the payload's tagCounts facet on every
// render. Those counts are independent of what is currently selected, so a chip
// never suppresses its own tag. A tag with zero matches in view is dimmed and
// non-interactive (unless already selected, so it can always be toggled back
// off) — shown, not hidden, so the filter state stays legible.
//
// A11Y: chips and the open-now control are native <button>s with aria-pressed,
// so they are keyboard-operable (Enter/Space) for free; the row is a labelled
// group.

import type { TagCount } from "../data/types";

/** Callback into the component so a filter change re-runs the viewport query. */
export interface FilterBarHandlers {
  /** The selected tags and/or open-now toggle changed; re-query with these. */
  onFiltersChange(tags: string[], openNow: boolean): void;
}

/** One chip's DOM handles, kept so counts/selection can be refreshed in place. */
interface Chip {
  button: HTMLButtonElement;
  countEl: HTMLElement;
}

export class FilterBar {
  readonly element: HTMLElement;

  private readonly chips = new Map<string, Chip>();
  private readonly selected = new Set<string>();
  private openNow = false;
  private readonly openNowButton: HTMLButtonElement;
  private readonly clearButton: HTMLButtonElement;

  constructor(
    private readonly tags: string[],
    private readonly handlers: FilterBarHandlers,
  ) {
    this.element = div("filter-bar");
    this.element.setAttribute("role", "group");
    this.element.setAttribute("aria-label", "Filter locations");

    const chipRow = div("filter-chips");
    for (const tagName of this.tags) {
      const chip = this.buildChip(tagName);
      this.chips.set(tagName, chip);
      chipRow.appendChild(chip.button);
    }

    // The open-now toggle is always meaningful, so it renders even when a locator
    // has no tags (the chip row is then simply empty).
    this.openNowButton = document.createElement("button");
    this.openNowButton.type = "button";
    this.openNowButton.className = "chip chip-opennow";
    this.openNowButton.setAttribute("aria-pressed", "false");
    this.openNowButton.textContent = "Open now";
    this.openNowButton.addEventListener("click", () => this.setOpenNow(!this.openNow));

    this.clearButton = document.createElement("button");
    this.clearButton.type = "button";
    this.clearButton.className = "filter-clear";
    this.clearButton.textContent = "Clear all";
    this.clearButton.hidden = true;
    this.clearButton.addEventListener("click", () => this.clearAll());

    this.element.append(chipRow, this.openNowButton, this.clearButton);
  }

  /** The currently selected tags, in the config's declared order. */
  selectedTags(): string[] {
    return this.tags.filter((tag) => this.selected.has(tag));
  }

  /** Whether the open-now toggle is on. */
  isOpenNow(): boolean {
    return this.openNow;
  }

  /**
   * Refreshes each chip's count from the current viewport facet. A tag absent
   * from [tagCounts] is treated as zero. Chips with zero matches are dimmed and
   * disabled unless selected (so a selected tag can always be toggled off).
   */
  updateCounts(tagCounts: TagCount[]): void {
    const byTag = new Map(tagCounts.map((entry) => [entry.tag, entry.count]));
    for (const [tagName, chip] of this.chips) {
      const count = byTag.get(tagName) ?? 0;
      chip.countEl.textContent = String(count);
      const isSelected = this.selected.has(tagName);
      chip.button.disabled = count === 0 && !isSelected;
      chip.button.classList.toggle("is-empty", count === 0);
    }
  }

  private buildChip(tagName: string): Chip {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "chip";
    button.setAttribute("aria-pressed", "false");

    const label = document.createElement("span");
    label.className = "chip-label";
    label.textContent = tagName;

    const countEl = document.createElement("span");
    countEl.className = "chip-count";

    button.append(label, countEl);
    button.addEventListener("click", () => this.toggleTag(tagName));
    return { button, countEl };
  }

  private toggleTag(tagName: string): void {
    const chip = this.chips.get(tagName);
    if (!chip) {
      return;
    }
    const nowSelected = !this.selected.has(tagName);
    if (nowSelected) {
      this.selected.add(tagName);
    } else {
      this.selected.delete(tagName);
    }
    chip.button.classList.toggle("is-selected", nowSelected);
    chip.button.setAttribute("aria-pressed", String(nowSelected));
    this.syncClearVisibility();
    this.emit();
  }

  private setOpenNow(value: boolean): void {
    this.openNow = value;
    this.openNowButton.classList.toggle("is-selected", value);
    this.openNowButton.setAttribute("aria-pressed", String(value));
    this.syncClearVisibility();
    this.emit();
  }

  private clearAll(): void {
    this.selected.clear();
    this.openNow = false;
    for (const chip of this.chips.values()) {
      chip.button.classList.remove("is-selected");
      chip.button.setAttribute("aria-pressed", "false");
    }
    this.openNowButton.classList.remove("is-selected");
    this.openNowButton.setAttribute("aria-pressed", "false");
    this.syncClearVisibility();
    this.emit();
  }

  /** "Clear all" only shows when there is something to clear. */
  private syncClearVisibility(): void {
    this.clearButton.hidden = this.selected.size === 0 && !this.openNow;
  }

  private emit(): void {
    this.handlers.onFiltersChange(this.selectedTags(), this.openNow);
  }
}

function div(className: string): HTMLElement {
  const node = document.createElement("div");
  node.className = className;
  return node;
}
